import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import RemoveIcon from "@mui/icons-material/Remove";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useSettings } from "../contexts/SettingsContext";
import { createNewSale, saveSale } from "../database/db";
import { BasketData } from "../utils/basket_helpers";

interface BasketProps {
  basketData: BasketData;
  setBasketData: (
    newDataOrUpdater: BasketData | ((prev: BasketData) => BasketData),
    regenerateBundles: boolean
  ) => void;
  showSnackbar: (message: string) => void;
}

export default function Basket({
  basketData,
  setBasketData,
  showSnackbar,
}: BasketProps) {
  const { settings } = useSettings();
  const [isEditingTotal, setIsEditingTotal] = useState(false);
  const [showCancelWarning, setShowCancelWarning] = useState(false);

  const subtotal = useMemo(
    () =>
      basketData.items
        .filter((item) => item.price >= 0)
        .reduce((sum, item) => sum + item.price * item.quantity, 0),
    [basketData.items]
  );

  const bundleSubtotal = useMemo(
    () =>
      basketData.bundles.reduce(
        (sum, bundle) => sum + bundle.discountAmount,
        0
      ),
    [basketData.bundles]
  );

  const discountSubtotal = useMemo(
    () =>
      basketData.items
        .filter((item) => item.price < 0)
        .reduce((sum, item) => sum + item.price * item.quantity, 0),
    [basketData.items]
  );

  const calculatedTotal = subtotal - bundleSubtotal + discountSubtotal;
  const total = basketData.adjustedTotal ?? calculatedTotal;

  const isNegativeTotal = useMemo(() => total < 0, [total]);

  const handleUpdateQuantity = (productId: number, newQuantity: number) => {
    setBasketData(
      (current) => ({
        ...current,
        items: current.items.map((item) =>
          item.id === productId ? { ...item, quantity: newQuantity } : item
        ),
      }),
      true
    );
  };

  const handleRemoveItem = (productId: number) => {
    setBasketData(
      (current) => ({
        ...current,
        items: current.items.filter((item) => item.id !== productId),
      }),
      true
    );
  };

  const handleSave = async () => {
    const newSale = createNewSale(basketData);
    await saveSale(newSale);
    showSnackbar(
      `Saved sale of ${settings.currencySymbol}${newSale.salePrice?.toFixed(
        settings.currencyRounding
      )}`
    );
    setBasketData(
      { items: [], adjustedTotal: null, bundles: [], notes: null },
      false
    );
  };

  const handleClearBasket = () => {
    setBasketData(
      { items: [], adjustedTotal: null, bundles: [], notes: null },
      false
    );
  };

  if (basketData.items.length === 0) {
    return (
      <Box sx={{ p: 2, height: "100%" }}>
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Click on products to add them to your basket
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Basket
      </Typography>
      <Stack spacing={0}>
        {basketData.items
          .filter((item) => item.price >= 0)
          .map((item) => {
            const imageUrl = item.imageBlob
              ? URL.createObjectURL(item.imageBlob)
              : null;
            return (
              <Box
                key={item.id}
                sx={{ p: 1, outline: "1px solid #e0e0e0", borderRadius: 1 }}>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    {imageUrl ? (
                      <Box
                        component="img"
                        src={imageUrl}
                        alt={item.name}
                        sx={{
                          width: 40,
                          height: 40,
                          objectFit: "cover",
                          borderRadius: 1,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "text.secondary",
                          backgroundColor: "action.hover",
                        }}>
                        <ImageNotSupportedIcon sx={{ opacity: 0.5 }} />
                      </Box>
                    )}
                    <Typography variant="body1">{item.name}</Typography>
                  </Stack>

                  {/* Second row: Quantity, Price, Delete */}
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between">
                    {/* Quantity controls */}
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleUpdateQuantity(item.id, item.quantity - 1)
                        }
                        disabled={item.quantity <= 1}>
                        <RemoveIcon />
                      </IconButton>
                      <Typography sx={{ mx: 1 }}>{item.quantity}</Typography>
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleUpdateQuantity(item.id, item.quantity + 1)
                        }>
                        <AddIcon />
                      </IconButton>
                    </Box>

                    {/* Price */}
                    <Typography>
                      {settings.currencySymbol}
                      {(item.price * item.quantity).toFixed(
                        settings.currencyRounding
                      )}
                    </Typography>

                    {/* Delete button */}
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveItem(item.id)}
                      color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </Stack>
              </Box>
            );
          })}

        {/* New Discounts Section */}
        {basketData.items.some((item) => item.price < 0) && (
          <>
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
              Discounts
            </Typography>
            {basketData.items
              .filter((item) => item.price < 0)
              .map((item) => (
                <Box
                  key={item.id}
                  sx={{ p: 1, outline: "1px solid #e0e0e0", borderRadius: 1 }}>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="body1">{item.name}</Typography>
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      justifyContent="space-between">
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleUpdateQuantity(item.id, item.quantity - 1)
                          }
                          disabled={item.quantity <= 1}>
                          <RemoveIcon />
                        </IconButton>
                        <Typography sx={{ mx: 1 }}>{item.quantity}</Typography>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleUpdateQuantity(item.id, item.quantity + 1)
                          }>
                          <AddIcon />
                        </IconButton>
                      </Box>

                      <Typography sx={{}}>
                        {settings.currencySymbol}
                        {(item.price * item.quantity).toFixed(
                          settings.currencyRounding
                        )}
                      </Typography>

                      <IconButton
                        size="small"
                        onClick={() => handleRemoveItem(item.id)}
                        color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Box>
              ))}
          </>
        )}
      </Stack>

      <Box sx={{ mt: 3, mb: 2 }}>
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between">
            <Typography color="text.secondary" fontWeight="bold">
              Subtotal:
            </Typography>
            <Typography fontWeight="bold">
              {settings.currencySymbol}
              {subtotal.toFixed(settings.currencyRounding)}
            </Typography>
          </Stack>

          {discountSubtotal !== 0 && (
            <Stack direction="row" justifyContent="space-between">
              <Typography color="success.main" fontWeight="bold">
                Discounts:
              </Typography>
              <Typography color="success.main" fontWeight="bold">
                {settings.currencySymbol}
                {discountSubtotal.toFixed(settings.currencyRounding)}
              </Typography>
            </Stack>
          )}

          {bundleSubtotal > 0 && (
            <>
              {/* Bundle details */}
              {basketData.bundles.map((bundle, index) => (
                <Stack
                  key={`${bundle.id}-${index}`}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ typography: "body2", color: "text.secondary" }}>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setBasketData(
                        (current) => ({
                          ...current,
                          bundles: current.bundles.filter(
                            (_, i) => i !== index
                          ),
                        }),
                        false
                      )
                    }>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                  <Typography sx={{ flexGrow: 1, textAlign: "left" }}>
                    {bundle.name}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography>
                      -{settings.currencySymbol}
                      {bundle.discountAmount.toFixed(settings.currencyRounding)}
                    </Typography>
                  </Stack>
                </Stack>
              ))}

              <Stack direction="row" justifyContent="space-between">
                <Typography color="success.main" fontWeight="bold">
                  Bundle Savings:
                </Typography>
                <Typography color="success.main" fontWeight="bold">
                  -{settings.currencySymbol}
                  {bundleSubtotal.toFixed(settings.currencyRounding)}
                </Typography>
              </Stack>
            </>
          )}

          <Box sx={{ pt: 1, borderTop: 1, borderColor: "divider" }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center">
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexGrow={1}>
                <IconButton
                  size="small"
                  onClick={() => setIsEditingTotal(true)}
                  title="Edit total">
                  <EditIcon />
                </IconButton>
                {basketData.adjustedTotal !== null && (
                  <IconButton
                    size="small"
                    onClick={() =>
                      setBasketData(
                        (current) => ({
                          ...current,
                          adjustedTotal: null,
                        }),
                        false
                      )
                    }
                    title="Reset to original">
                    <RestartAltIcon />
                  </IconButton>
                )}
              </Stack>
              <Typography variant="h6" flexShrink={1}>
                Total:
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Stack
                  direction="column"
                  alignItems="flex-start"
                  justifyContent="flex-start"
                  paddingLeft={2}
                  flexGrow={1}>
                  {basketData.adjustedTotal !== null && (
                    <Typography
                      variant="h6"
                      sx={{
                        textDecoration: "line-through",
                        fontSize: "1em",
                      }}
                      color="text.secondary">
                      {settings.currencySymbol}
                      {total.toFixed(settings.currencyRounding)}
                    </Typography>
                  )}
                  <Typography
                    variant="h6"
                    color={
                      isNegativeTotal
                        ? "error"
                        : basketData.adjustedTotal !== null
                        ? "primary"
                        : "inherit"
                    }>
                    {settings.currencySymbol}
                    {total.toFixed(settings.currencyRounding)}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ mt: 2 }}>
        <TextField
          label="Notes"
          multiline
          rows={3}
          value={basketData.notes ?? ""}
          onChange={(e) =>
            setBasketData(
              (current) => ({ ...current, notes: e.target.value }),
              false
            )
          }
          fullWidth
        />
      </Box>

      <Stack
        direction="row"
        spacing={2}
        justifyContent="flex-end"
        marginTop={2}>
        <Button
          variant="outlined"
          color="error"
          onClick={() => setShowCancelWarning(true)}>
          Clear
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={isNegativeTotal}>
          Save
        </Button>
      </Stack>

      <Dialog open={isEditingTotal} onClose={() => setIsEditingTotal(false)}>
        <DialogTitle>Adjust Total Amount</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Total"
            type="number"
            fullWidth
            value={basketData.adjustedTotal?.toString() ?? total.toString()}
            onChange={(e) =>
              setBasketData(
                (current) => ({
                  ...current,
                  adjustedTotal: parseFloat(e.target.value) || 0,
                }),
                false
              )
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsEditingTotal(false);
            }}>
            Cancel
          </Button>
          <Button onClick={() => setIsEditingTotal(false)}>Confirm</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showCancelWarning}
        onClose={() => setShowCancelWarning(false)}>
        <DialogTitle>Cancel Order?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel this order? This will clear all
            items from the basket.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelWarning(false)}>No</Button>
          <Button
            onClick={() => {
              setShowCancelWarning(false);
              handleClearBasket();
            }}
            color="error">
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
